import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.tsx';

// A custom dropdown built from the app's own tokens. A native <select> renders
// its option popup with the OS colour scheme (showing up black/grey on desktop),
// so we roll our own for a consistent look across light/dark themes.
export function Dropdown({ value, options, placeholder, onChange, className }: {
  value?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);
  const selected = options.find((o) => o.value === value);
  return (
    <div className={`relative ${className ?? ''}`} ref={ref}>
      <button type="button" className="input flex w-full items-center justify-between gap-2 text-start"
        onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <span className={`truncate ${selected ? '' : 'text-muted-foreground'}`}>{selected?.label ?? placeholder}</span>
        <Icon name="expand_more" size={20} className="shrink-0 opacity-60" />
      </button>
      {open && (
        <ul role="listbox" className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-2xl bg-[hsl(var(--surface-container-low))] p-1 shadow-lg">
          {options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">—</li>
          ) : options.map((o) => (
            <li key={o.value}>
              <button type="button" role="option" aria-selected={o.value === value}
                className={`block w-full truncate rounded-xl px-3 py-2 text-start text-sm hover:bg-[hsl(var(--surface-high))] ${o.value === value ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>{o.label}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
